import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MutiTreatmentListDto } from './api-service/register/mutiTreatmentListDto';
import { PatientListDto } from './api-service/patient/patient-api';
import { ReserveItem } from './reserve.service';
import { UserCache } from './user-cache';

export interface PatientInfoWindowLayout {
  side: 'top' | 'right' | 'bottom' | 'left';
  size: number;
}

export interface PatientInfoWindowState {
  visible: boolean;
  selectedPatient?: PatientListDto;
  selectedRegisterItem?: any;
  patientId?: number;
  enableEdit?: boolean;
  memoryScope?: string;
  refreshToken?: number;
  refreshPatientId?: number;
}

export interface PatientInfoWindowEditRequest {
  patient: PatientListDto;
  registerItem?: any;
}

export interface PatientInfoWindowReserveRequest {
  patient: PatientListDto;
  registerItem?: any;
}

export interface PatientInfoWindowReserveRecordRequest {
  patient?: PatientListDto;
  patientId?: number;
  reserveItem?: ReserveItem;
  reserveMatters?: string;
  targetDate?: Date | string;
}

export interface PatientInfoWindowDentalFollowRequest {
  patient: PatientListDto;
  treatment: MutiTreatmentListDto;
}

export interface PatientInfoPetBirthdayReference {
  source: 'waiting-list' | 'dental-reserve-dialog';
  patientId: number;
  referenceDate: Date;
  sequence: number;
}

@Injectable({
  providedIn: 'root'
})
export class PatientInfoWindowService {
  private readonly memoryKeyPrefix = 'patient-info-window';
  private readonly legacyMemoryKeyPrefix = 'waiting-list-dentistry-patient-info';
  private readonly stateSubject = new BehaviorSubject<PatientInfoWindowState>({
    visible: false,
    selectedPatient: null,
    selectedRegisterItem: null,
    patientId: 0,
    enableEdit: true,
    memoryScope: 'global',
    refreshToken: 0,
    refreshPatientId: 0
  });

  private refreshSequence = 0;
  private readonly selectedPatientChangeSubject = new Subject<PatientListDto>();
  private readonly editPatientSubject = new Subject<PatientInfoWindowEditRequest>();
  private readonly reserveRecordNavigationSubject = new Subject<PatientInfoWindowReserveRecordRequest>();
  private readonly dentalFollowSubject = new Subject<PatientInfoWindowDentalFollowRequest>();
  private readonly layoutChangeSubject = new BehaviorSubject<PatientInfoWindowLayout | null>(null);
  private readonly resizeStateChangeSubject = new BehaviorSubject<boolean>(false);
  private readonly petBirthdayReferenceSubject = new BehaviorSubject<PatientInfoPetBirthdayReference | null>(null);
  private petBirthdayReferenceSequence = 0;
  private pendingReserveRecordNavigation: PatientInfoWindowReserveRecordRequest = null;

  readonly state$ = this.stateSubject.asObservable();
  readonly selectedPatientChange$ = this.selectedPatientChangeSubject.asObservable();
  readonly editPatient$ = this.editPatientSubject.asObservable();
  readonly reserveRecordNavigation$ = this.reserveRecordNavigationSubject.asObservable();
  readonly dentalFollow$ = this.dentalFollowSubject.asObservable();
  readonly layoutChange$ = this.layoutChangeSubject.asObservable();
  readonly resizeStateChange$ = this.resizeStateChangeSubject.asObservable();
  readonly petBirthdayReference$ = this.petBirthdayReferenceSubject.asObservable();

  get snapshot(): PatientInfoWindowState {
    return this.stateSubject.value;
  }

  show(state: Partial<PatientInfoWindowState>) {
    this.patchState({ ...state, visible: true });
  }

  setRouteVisible(visible: boolean) {
    if (this.snapshot.visible === visible) {
      return;
    }

    this.patchState({ visible });
  }

  emitSelectedPatientChange(patient: PatientListDto) {
    this.patchSelectedPatient(patient);
    this.selectedPatientChangeSubject.next(patient);
  }

  emitEditPatient(request: PatientInfoWindowEditRequest) {
    this.editPatientSubject.next(request);
  }

  requestReserveRecordNavigation(request: PatientInfoWindowReserveRecordRequest) {
    if (!request?.reserveItem && !request?.reserveMatters && !request?.targetDate) {
      return;
    }

    this.pendingReserveRecordNavigation = request;
    this.reserveRecordNavigationSubject.next(request);
  }

  consumePendingReserveRecordNavigation(): PatientInfoWindowReserveRecordRequest {
    const request = this.pendingReserveRecordNavigation;
    this.pendingReserveRecordNavigation = null;
    return request;
  }

  emitDentalFollow(request: PatientInfoWindowDentalFollowRequest) {
    this.dentalFollowSubject.next(request);
  }

  emitLayoutChange(layout: PatientInfoWindowLayout | null) {
    this.layoutChangeSubject.next(layout);
  }

  emitResizeStateChange(resizing: boolean) {
    this.resizeStateChangeSubject.next(resizing);
  }

  setPetBirthdayReference(source: PatientInfoPetBirthdayReference['source'], patientId: number,
    referenceDate: Date | string | null | undefined) {
    const date = referenceDate instanceof Date
      ? new Date(referenceDate.getTime())
      : (referenceDate ? new Date(referenceDate) : new Date(NaN));
    const normalizedPatientId = Number(patientId || 0);
    if (!normalizedPatientId || !Number.isFinite(date.getTime())) {
      this.clearPetBirthdayReference(source);
      return;
    }

    this.petBirthdayReferenceSubject.next({
      source,
      patientId: normalizedPatientId,
      referenceDate: date,
      sequence: ++this.petBirthdayReferenceSequence
    });
  }

  clearPetBirthdayReference(source: PatientInfoPetBirthdayReference['source']) {
    if (this.petBirthdayReferenceSubject.value?.source === source) {
      this.petBirthdayReferenceSubject.next(null);
    }
  }

  getPinnedContentStyle(layout: PatientInfoWindowLayout | null | undefined, reserveGap = 8): { [key: string]: string } {
    if (!layout) {
      return {};
    }

    const size = `${Math.round(Number(layout.size || 0) + reserveGap)}px`;
    switch (layout.side) {
      case 'left':
        return { 'margin-left': size };
      case 'right':
        return { 'margin-right': size };
      case 'top':
        return { 'margin-top': size };
      case 'bottom':
        return { 'margin-bottom': size };
      default:
        return {};
    }
  }

  requestRefresh(patientId?: number) {
    const refreshPatientId = Number(patientId || this.snapshot.patientId || this.snapshot.selectedPatient?.Id || 0);
    this.patchState({
      refreshPatientId,
      refreshToken: ++this.refreshSequence
    });
  }

  setSelectedPatient(patient: PatientListDto) {
    this.patchSelectedPatient(patient);
  }

  getRememberedSelectedPatient(memoryScope = 'global'): PatientListDto | null {
    try {
      const raw = localStorage.getItem(this.getMemoryKey(memoryScope))
        || localStorage.getItem(this.getLegacyMemoryKey());
      if (raw) {
        const patient = JSON.parse(raw)?.selectedPatient;
        if (patient?.Id) {
          return Object.assign(new PatientListDto(), patient);
        }
      }
    } catch (ex) {
      // Ignore invalid or unavailable localStorage and fall back to current state.
    }

    const snapshotPatient = this.snapshot.selectedPatient;
    return snapshotPatient?.Id ? Object.assign(new PatientListDto(), snapshotPatient) : null;
  }

  private patchState(state: Partial<PatientInfoWindowState>) {
    this.stateSubject.next({
      ...this.snapshot,
      ...state
    });
  }

  private patchSelectedPatient(patient: PatientListDto) {
    if (!patient) {
      return;
    }

    const selectedPatient = Object.assign(new PatientListDto(), patient);
    this.saveSelectedPatientToMemory(selectedPatient);
    this.patchState({
      selectedPatient,
      patientId: Number(selectedPatient.Id || this.snapshot.patientId || 0)
    });
  }

  private saveSelectedPatientToMemory(patient: PatientListDto, memoryScope = 'global') {
    try {
      const key = this.getMemoryKey(memoryScope);
      const memory = JSON.parse(localStorage.getItem(key) || '{}');
      localStorage.setItem(key, JSON.stringify({
        ...memory,
        selectedPatient: patient
      }));
    } catch (ex) {
      // localStorage quota or privacy settings should not block page workflow.
    }
  }

  private getMemoryKey(memoryScope: string): string {
    const loginUser = UserCache.getLoginUser();
    const accountKey = loginUser?.UserId || loginUser?.CId || 'anonymous';
    return `${this.memoryKeyPrefix}-${memoryScope || 'global'}-${UserCache.getLoginClinicId()}-${accountKey}`;
  }

  private getLegacyMemoryKey(): string {
    const loginUser = UserCache.getLoginUser();
    const accountKey = loginUser?.UserId || loginUser?.CId || 'anonymous';
    return `${this.legacyMemoryKeyPrefix}-${UserCache.getLoginClinicId()}-${accountKey}`;
  }
}
